import cn from 'classnames';
import { MobileStepper, Step, StepLabel, Stepper } from '@mui/material';
import lockIconSrc from '@packages/shared/assets/simple-registration/lock.svg';
import Image from '@packages/shared/core/common/components/common/Image';
import { ObjectValues } from '@packages/shared/core/common/utils/types';
import styles from './SplitProgressBar.module.scss';

export const splitProgressBarStepNames = {
  profile: 'profile',
  personality: 'personality',
  preferences: 'preferences',
  finished: 'finished',
} as const;

export type SplitProgressBarStepNames = ObjectValues<typeof splitProgressBarStepNames>;

export const splitProgressBarSteps: Record<SplitProgressBarStepNames, number> = {
  profile: 0,
  personality: 1,
  preferences: 2,
  finished: 3,
};

type ProgressBarHintProps = {
  currentName?: SplitProgressBarStepNames;
  currentStep: number;
  combinedCurrentStep?: number;
  title?: string;
  totalSteps?: number;
  totalProfileSteps?: number;
  totalPersonalitySteps?: number;
  totalPreferencesSteps?: number;
};

const ProgressBarHint = ({
  currentName = 'profile',
  currentStep = 3,
  combinedCurrentStep = 1,
  totalSteps = 25,
  totalProfileSteps = 4,
  totalPersonalitySteps = 13,
  totalPreferencesSteps = 10,
}: ProgressBarHintProps) => {
  const passiveProgressBarColor = 'var(--primary-800)';
  const activeStep = splitProgressBarSteps[currentName];

  const totalCurrentStep = Math.min(combinedCurrentStep, totalSteps);

  const profileClasses = cn({
    [styles.mobileStepper]: true,
    [styles.profile]: true,
    [styles.active]: activeStep === 0,
  });

  const relationshipClasses = cn({
    [styles.mobileStepper]: true,
    [styles.personality]: true,
    [styles.active]: activeStep === 1,
  });

  return (
    <>
      <div className={styles.progressBarHeader}>
        <div className={styles.disclaimer}>
          <Image type="future" src={lockIconSrc} width={10} height={10} alt="" className={styles.disclaimerIcon} />

          <span className={styles.disclaimerText}>Your answers are confidential</span>
        </div>

        <div className={styles.progressCounter}>{`${totalCurrentStep}/${totalSteps}`}</div>
      </div>

      <Stepper activeStep={activeStep} connector={<></>} className={styles.stepper}>
        <Step active={activeStep === splitProgressBarSteps.profile} className={styles.step} completed>
          <div className={cn(styles.circle, styles.completed)}>1</div>

          <StepLabel className={styles.stepLabel} />

          <MobileStepper
            color={passiveProgressBarColor}
            variant="progress"
            steps={totalProfileSteps}
            position="static"
            activeStep={activeStep === splitProgressBarSteps.profile ? currentStep : totalProfileSteps - 1}
            backButton={<div />}
            nextButton={<div />}
            className={profileClasses}
          />
        </Step>

        <Step
          active={activeStep === splitProgressBarSteps.personality}
          className={styles.step}
          completed={activeStep >= splitProgressBarSteps.personality}
        >
          <div
            className={cn(styles.circle, {
              [styles.completed]: activeStep >= 1,
            })}
          >
            2
          </div>

          <StepLabel className={styles.stepLabel} />

          <MobileStepper
            color={passiveProgressBarColor}
            variant="progress"
            steps={totalPersonalitySteps}
            position="static"
            activeStep={
              activeStep === splitProgressBarSteps.personality
                ? currentStep
                : activeStep === splitProgressBarSteps.profile
                  ? 0
                  : totalPersonalitySteps - 1
            }
            backButton={<div />}
            nextButton={<div />}
            className={relationshipClasses}
          />
        </Step>

        <Step
          active={activeStep === splitProgressBarSteps.preferences}
          className={styles.step}
          completed={activeStep >= splitProgressBarSteps.preferences}
        >
          <div
            className={cn(styles.circle, {
              [styles.completed]: activeStep >= 2,
            })}
          >
            3
          </div>

          <StepLabel className={styles.stepLabel} />

          <MobileStepper
            color={passiveProgressBarColor}
            variant="progress"
            steps={totalPreferencesSteps}
            position="static"
            activeStep={activeStep < splitProgressBarSteps.preferences ? 0 : currentStep}
            backButton={<div />}
            nextButton={<div />}
            className={cn(styles.mobileStepper, styles.preferences, {
              [styles.active]: activeStep === 2,
            })}
          />
        </Step>

        <Step active={activeStep >= splitProgressBarSteps.finished} className={styles.step}>
          <div
            className={cn(styles.circle, {
              [styles.completed]: activeStep > 2,
            })}
          >
            4
          </div>

          <StepLabel className={styles.stepLabel} />
        </Step>
      </Stepper>
    </>
  );
};

export default ProgressBarHint;
